import React from "react";
import { Skeleton } from "../skeleton";

export const CoursePreviewSkeleton: React.FC = () => {
    return (
        <div className="flex h-screen flex-col overflow-hidden">
            {/* Top bar */}
            <div className="border-border bg-background flex h-12 shrink-0 items-center justify-between border-b px-4">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-24" />
                    <div className="bg-border h-4 w-px" />
                    <Skeleton className="h-4 w-40" />
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-7 w-20 rounded-md" />
                    <Skeleton className="h-7 w-20 rounded-md" />
                    <Skeleton className="h-7 w-20 rounded-md" />
                </div>
            </div>

            {/* Body */}
            <div className="flex flex-1 overflow-hidden">
                {/* Main content */}
                <div className="bg-muted/30 flex-1 overflow-y-auto p-8">
                    {/* Thumbnail + title row */}
                    <div className="flex items-start gap-6">
                        <Skeleton
                            className="max-h-48 w-full max-w-xs shrink-0 rounded-xl"
                            style={{ aspectRatio: "16/9" }}
                        />
                        <div className="min-w-0 flex-1">
                            <Skeleton className="mb-3 h-5 w-20 rounded-full" />
                            <Skeleton className="mb-2 h-9 w-3/4" />
                            <Skeleton className="mb-1 h-5 w-full" />
                            <Skeleton className="mb-3 h-5 w-2/3" />
                            <Skeleton className="h-3 w-32" />
                        </div>
                    </div>

                    {/* Two-column layout */}
                    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
                        {/* Left: curriculum */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <Skeleton className="h-5 w-28" />
                                <Skeleton className="h-3 w-48" />
                            </div>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="border-border bg-card rounded-xl border p-4">
                                    <div className="mb-3 flex items-center justify-between">
                                        <Skeleton className="h-4 w-32" />
                                        <Skeleton className="h-4 w-16" />
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {Array.from({ length: 2 }).map((_, j) => (
                                            <div key={j} className="flex items-center gap-3">
                                                <Skeleton className="h-7 w-7 shrink-0 rounded-md" />
                                                <Skeleton className="h-4 flex-1" />
                                                <Skeleton className="h-4 w-12" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Right: detail cards */}
                        <div className="flex flex-col gap-4">
                            {/* Details card */}
                            <div className="border-border bg-card rounded-xl border p-4">
                                <Skeleton className="mb-4 h-4 w-16" />
                                <div className="flex justify-between gap-4">
                                    <div className="flex flex-col gap-2.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Skeleton key={i} className="h-3 w-20" />
                                        ))}
                                    </div>
                                    <div className="flex flex-col items-end gap-2.5">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Skeleton key={i} className="h-3 w-28" />
                                        ))}
                                    </div>
                                </div>
                                <div className="mt-3 flex gap-2">
                                    <Skeleton className="h-6 w-20 rounded-full" />
                                    <Skeleton className="h-6 w-20 rounded-full" />
                                </div>
                            </div>

                            {/* Presenters card */}
                            <div className="border-border bg-card rounded-xl border p-4">
                                <Skeleton className="mb-1 h-4 w-24" />
                                <Skeleton className="mb-4 h-3 w-48" />
                                <div className="flex items-center gap-2">
                                    <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
                                    <div>
                                        <Skeleton className="mb-1 h-3 w-28" />
                                        <Skeleton className="h-3 w-20" />
                                    </div>
                                </div>
                            </div>

                            {/* Tags card */}
                            <div className="border-border bg-card rounded-xl border p-4">
                                <Skeleton className="mb-1 h-4 w-12" />
                                <Skeleton className="mb-4 h-3 w-40" />
                                <div className="flex flex-wrap gap-1.5">
                                    <Skeleton className="h-6 w-16 rounded-full" />
                                    <Skeleton className="h-6 w-20 rounded-full" />
                                    <Skeleton className="h-6 w-14 rounded-full" />
                                </div>
                            </div>

                            {/* Visibility card */}
                            <div className="border-border bg-card rounded-xl border p-4">
                                <Skeleton className="mb-4 h-4 w-36" />
                                <div className="flex flex-col gap-4">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div>
                                                <Skeleton className="mb-1 h-3 w-32" />
                                                <Skeleton className="h-3 w-44" />
                                            </div>
                                            <Skeleton className="h-5 w-9 shrink-0 rounded-full" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
